import React, { useEffect, useState } from "react";

//** Context  */
import usePreQualification from "../../../hooks/usePreQualification.ts";
import usePreQualificationPages from "../../../hooks/usePreQualificationPages.ts";
import useSharedData from "../../../hooks/useSharedData.ts";

//* Hooks
import { useGetQuote } from "../../../hooks/dataHooks/useGetQuoteData.js";

//** NDBX Components */
import {
	Button,
	ButtonStack,
	OneColumnStack,
	NotificationCard,
	RHFCircleToggleGroupChildren,
	RHFTextField,
	Section,
} from "@arp/react-ndbx";

//** MUI components */
import { Box, Typography } from "@mui/material";

//** App components */
import Page from "../../../components/Page";
import ButtonNavigationGroup from "../../../components/organisms/ButtonNavigationGroup.jsx";
import TextFieldNotification from "../../../components/organisms/TextFieldNotification.jsx";
import ToggleButtonNotification from "../../../components/organisms/ToggleButtonNotification.jsx";
import AddressDetailsChoice from "../../../components/pageLevel/paymentContactDetails/AddressDetailsChoice.jsx";
import PaymentContactDetailsHeader from "../../../components/pageLevel/paymentContactDetails/PaymentContactDetailsHeader.jsx";

//** React Hooks */
import { useWatch } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

//** Config */
import { paymentContactDetailsNotification } from "../../../config/notifications/pageNotifications";
import { paymentNavigationContactDetails } from "../../../config/notifications/pageNotifications";
import {
	paymentContactDetailsFieldNames,
	yourDetailsContactFieldNames,
} from "../../../config/pageInfo/pageFieldName.js";
import { residentialAddress } from "../../../config/questions/questionComponents.jsx";

import {
	questionLabels,
	questionTypes,
} from "../../../config/questions/questionItems.js";

import { Application } from "../../../config/const/enums.js";
import { paymentContactDetailsDisclaimers } from "../../../config/disclaimers/disclaimers.jsx";

import { paymentContactTooltips } from "../../../config/tooltips/pageTooltips";
import { trackifyPageChange } from "@allianz/tracking";

//----------------------------------------------------------------
export default function PaymentContactDetails() {
	const { state } = usePreQualificationPages();
	const { currentPage } = { ...state };
	const { dataSet } = usePreQualification();

	// const { accessToken, state: sharedState } = useSharedData();
	const { accessToken, retrieveQuoteJourney } = useSharedData();

	const { isLoading, isFetching, data, error } = useGetQuote();
	const { quoteInformation } = {
		...data,
	};

	const [showAddressComponent, setShowAddressComponent] = useState(false);
	const {
		methods: [methods],
		isDirty: [isDirty],
		isValid: [isValid],
		errors: [errors],
		onBackButton: [onBackButton],
		handleAddressEntryType: [handleAddressEntryType],
	} = useOutletContext();

	const { control, resetField, trigger, setValue } = { ...methods };

	useEffect(() => {
		//* We want to validate the page
		//* when we move back on to it.
		//* the valid flag tells us that we have completed this page
		//* and all validation was complete.
		if (currentPage?.valid === true) {
			trigger();
		}
	}, [trigger]);

	const otherResidentialAddress = useWatch({
		control,
		name: "bothAddressesTheSame",
	});

	const [resetAll, setResetAll] = useState(false);
	useEffect(() => {
		setShowAddressComponent(
			otherResidentialAddress == null ||
				otherResidentialAddress?.toLowerCase() === "yes"
				? false
				: true
		);
		setResetAll(true);
		handleSwitchView(
			paymentContactDetailsFieldNames().RESIDENTIAL_ADDRESS_BASE
		);
	}, [otherResidentialAddress]);

	useEffect(() => {
		setResetAll(false);

		//** TODO: Should we check for retrieveQuoteJourney? */
		// if (retrieveQuoteJourney === true) {
		setValue(
			yourDetailsContactFieldNames("personOne").CONTACT_NUMBER,
			dataSet["personOne"]?.contactNumber,
			{ shouldDirty: true }
		);

		setValue(
			yourDetailsContactFieldNames("personOne").EMAIL_ADDRESS,
			dataSet?.["personOne"]?.emailAddress,
			{ shouldDirty: true }
		);
		// }
		trackifyPageChange(currentPage?.identifier?.toLowerCase());
	}, []);

	//* We need to the React Hook Form values for the full address
	const handlePolicyMailingOnChange = (e) => {
		methods.setValue(
			paymentContactDetailsFieldNames().POLICY_MAILING_ADDRESS_FULL,
			e
		);
	};

	const handleResidentialMailingOnChange = (e) => {
		methods.setValue(
			paymentContactDetailsFieldNames().RESIDENTIAL_ADDRESS_FULL,
			e
		);
	};

	const handlePolicyManualPostcode = (e) => {
		methods.setValue(
			paymentContactDetailsFieldNames().POLICY_MAILING_ADDRESS_POSTCODE_SUBURB,
			e
		);
	};

	const handleResidentialManualPostcode = (e) => {
		methods.setValue(
			paymentContactDetailsFieldNames().RESIDENTIAL_ADDRESS_POSTCODE_SUBURB,
			e
		);
	};

	const onHandleMailingAddress = (e) => {
		handleAddressEntryType({ name: e.name, value: e.value });
	};

	const handleSwitchView = (fieldName) => {
		//* When we switch between views, reset all the address fields
		resetField(`${fieldName}Full`, "");
		resetField(`${fieldName}LevelNumber`, "");
		resetField(`${fieldName}UnitNumber`, "");
		resetField(`${fieldName}StreetNumber`, "");
		resetField(`${fieldName}StreetNumberType`, "");
		resetField(`${fieldName}StreetName`, "");
		resetField(`${fieldName}StreetNameType`, "");
		resetField(`${fieldName}PostcodeSuburb`, "");
		// resetField(`${fieldName}State`, "");
		// resetField(`${fieldName}Suburb`, "");
	};

	const policyInformationWatch = useWatch({
		control,
		name: "policyDocumentRequestType",
	});

	const [showPolicyInformation, setShowPolicyInformation] = useState(false);

	useEffect(() => {
		const outputPolicyInformation =
			policyInformationWatch?.toLowerCase() === "no";
		setShowPolicyInformation(outputPolicyInformation);
	}, [policyInformationWatch]);

	// useEffect(() => {
	// 	trackifyPageChange(currentPage?.identifier?.toLowerCase());
	// }, []);

	//** Communication Consent - This is a placeholder. May not be used in Prod  */
	// const isContactCenter = dataSet?.application === Application.CONTACT_CENTER;
	// let shouldHideConsent =
	// 	(retrieveQuoteJourney === true && dataSet[person]?.callbackConsent) ||
	// 	isContactCenter === true;
	// const isProdReady = false; //? NB: Only set to true, when ready to go in to Production
	//** Communication Consent - This is a placeholder. May not be used in Prod  */

	//----------------------------------------------------------------
	return (
		<Page title={currentPage.title} identifier={currentPage.identifier}>
			<Section>
				<PaymentContactDetailsHeader
					policyReferenceNumber={quoteInformation?.quoteNumber}
					firstName={dataSet?.["personOne"]?.firstName}
				/>
			</Section>
			<Section mb={{ xs: 2, md: 6 }}>
				<AddressDetailsChoice
					name={paymentContactDetailsFieldNames().POLICY_MAILING_ADDRESS_BASE}
					accessToken={accessToken}
					onChange={(e) => handlePolicyMailingOnChange(e)}
					onSwitchView={(e) => handleSwitchView(e)}
					dataSet={dataSet}
					handleAddressEntryType={(e) => {
						onHandleMailingAddress(e);
					}}
					onAutoCompleteChange={(e) => {
						handlePolicyManualPostcode(e);
					}}
					au
					autoCompleteLabelOption="fullPostcode"
					autoCompleteName={
						paymentContactDetailsFieldNames()
							.POLICY_MAILING_ADDRESS_POSTCODE_SUBURB
					}
				/>
			</Section>
			<OneColumnStack sx={{ maxWidth: { xs: 636 } }}>
				<Section>
					<RHFCircleToggleGroupChildren
						variant="circle-group"
						name={paymentContactDetailsFieldNames().BOTH_ADDRESSES_THE_SAME}
						items={questionTypes.YES_NO}
						sx={{ mt: 0, textAlign: "left" }}
						children={residentialAddress}
						position={{ xs: "center", sm: "left" }}
						// defaultValue="yes"
						// error={error}
					></RHFCircleToggleGroupChildren>
				</Section>
				{showAddressComponent ? (
					<Section mb={6}>
						<AddressDetailsChoice
							name={paymentContactDetailsFieldNames().RESIDENTIAL_ADDRESS_BASE}
							accessToken={accessToken}
							onChange={(e) => handleResidentialMailingOnChange(e)}
							onSwitchView={(e) => handleSwitchView(e)}
							dataSet={dataSet}
							showTitle={false}
							// resetAll={resetAll}
							handleAddressEntryType={(e) => {
								onHandleMailingAddress(e);
							}}
							onAutoCompleteChange={(e) => {
								handleResidentialManualPostcode(e);
							}}
							autoCompleteLabelOption="fullPostcode"
							autoCompleteName={
								paymentContactDetailsFieldNames()
									.RESIDENTIAL_ADDRESS_POSTCODE_SUBURB
							}
						/>
					</Section>
				) : null}
			</OneColumnStack>
			<OneColumnStack sx={{ maxWidth: { xs: 636 }, marginTop: 2 }}>
				<Section>
					<Box>
						<Section align="start">
							<Typography
								sx={{
									width: "fit-content !important",
									lineHeight: "20px",
								}}
								variant="h5"
							>
								Policy Contact Details
							</Typography>
						</Section>
						<Section align="start" mt={3}>
							<Typography
								sx={{
									width: "fit-content !important",
								}}
								variant="p3"
							>
								Confirm your mobile and email
							</Typography>
						</Section>
					</Box>
				</Section>
				<Section md={12} mt={{ sm: 2, md: 4 }}>
					<TextFieldNotification
						className="subvariant-text-input"
						variant="standard"
						label="Mobile number"
						name={yourDetailsContactFieldNames("personOne").CONTACT_NUMBER}
						defaultValue={
							dataSet != null ? dataSet["personOne"].contactNumber : ""
						} //? This will be from the Person 1 email already entered
						type="phone"
						showTooltip={true}
						tooltipText={paymentContactTooltips.CHANGE_MOBILE_NUMBER_TOOLTIP}
						showNotification={false}
					/>
				</Section>
				<Section md={12} mt={{ sm: 2, md: 4 }}>
					<RHFTextField
						className="subvariant-text-input"
						variant="standard"
						label="Email address"
						defaultValue={
							dataSet != null ? dataSet["personOne"].emailAddress : ""
						} //? This will be from the Person 1 email already entered
						name={yourDetailsContactFieldNames("personOne").EMAIL_ADDRESS}
						// type="email"
						showTooltip={false}
					/>
				</Section>
				<Section mt={2} sx={{ textAlign: "start" }}>
					<Typography
						sx={{
							width: "fit-content !important",
						}}
						variant="p3"
					>
						{
							paymentContactDetailsDisclaimers({
								isOnline:
									dataSet?.application === Application.ONLINE ? true : false,
							}).emailInfo[0].EMAIL_INFORMATION.note
						}
					</Typography>
				</Section>
			</OneColumnStack>
			{dataSet?.application === Application.CONTACT_CENTER ? (
				<OneColumnStack sx={{ maxWidth: { xs: 636 }, mt: 3 }}>
					<Section>
						<ToggleButtonNotification
							dataSetItem={dataSet?.policyDocumentRequestType}
							fieldName={
								paymentContactDetailsFieldNames().POLICY_DOCUMENT_REQUEST_TYPE
							}
							label={questionLabels.CC_EMAIL_POLICY_INFO}
							items={questionTypes.YES_NO}
							notificationCard={
								paymentContactDetailsNotification.EMAIL_POLICY_INFO_NOTIFICATION
							}
							showNotification={showPolicyInformation}
							showTooltip={false}
							position="start"
							labelVariant="p2"
						/>
					</Section>
				</OneColumnStack>
			) : null}
			{dataSet?.application === Application.ONLINE ? (
				<OneColumnStack sx={{ maxWidth: { xs: 636 }, mt: 3 }}>
					<Section>
						<NotificationCard
							cardType={
								paymentNavigationContactDetails.CONTACT_DETAILS_NAVIGATION
									.cardType
							}
							title={
								paymentNavigationContactDetails.CONTACT_DETAILS_NAVIGATION.title
							}
							message={
								paymentNavigationContactDetails.CONTACT_DETAILS_NAVIGATION
									.message
							}
						/>
					</Section>
				</OneColumnStack>
			) : null}
			<Section mt={6}>
				<ButtonStack>
					<Button
						variant="contained"
						color="secondary"
						// disabled={!isDirty || !isValid || isLoading}
						// onClick={handleSendEmailOpen}
					>
						Save
					</Button>
					<Button
						variant="contained"
						color="primary"
						type="submit"
						disabled={!isDirty || !isValid}
					>
						Proceed to payments
					</Button>
				</ButtonStack>
				<Section mt={2}>
					<ButtonNavigationGroup showNextButton={false} />
				</Section>
			</Section>
		</Page>
	);
}
